import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Skeleton,
  Tabs,
  Tab
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area
} from 'recharts';

// ============================================
// Type Definitions
// ============================================

interface ChartDataItem {
  name?: string;
  department?: string;
  gender?: string;
  experience_band?: string;
  age_band?: string;
  count: number;
  value?: number;
}

interface WorkforceChartsProps {
  departmentData?: ChartDataItem[];
  genderData?: ChartDataItem[];
  experienceData?: ChartDataItem[];
  ageData?: ChartDataItem[];
  loading?: boolean;
}

// ============================================
// Color Palettes
// ============================================

const DEPARTMENT_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336',
  '#00BCD4', '#E91E63', '#3F51B5', '#8BC34A', '#FF5722'
];

const GENDER_COLORS = {
  male: '#2196F3',
  female: '#E91E63',
  other: '#9C27B0',
  not_specified: '#9E9E9E',
  prefer_not_to_say: '#607D8B'
};

const EXPERIENCE_COLORS = [
  '#E3F2FD', '#BBDEFB', '#90CAF9', '#64B5F6', '#42A5F5', '#2196F3'
];

const AGE_COLORS = [
  '#FCE4EC', '#F8BBD9', '#F48FB1', '#F06292', '#EC407A', '#E91E63', '#D81B60'
];

// ============================================
// Department Chart Component
// ============================================

interface DepartmentChartProps {
  data: ChartDataItem[];
  loading?: boolean;
  height?: number;
}

export const DepartmentChart: React.FC<DepartmentChartProps> = ({
  data,
  loading = false,
  height = 300
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const chartData = data.map((item, index) => ({
    name: item.department || item.name || 'Unknown',
    count: item.count,
    fill: DEPARTMENT_COLORS[index % DEPARTMENT_COLORS.length]
  }));

  if (loading) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Skeleton variant="rectangular" width="100%" height={height - 40} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#444' : '#eee'} />
          <XAxis type="number" stroke={theme.palette.text.secondary} />
          <YAxis
            type="category"
            dataKey="name"
            width={100}
            stroke={theme.palette.text.secondary}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#333' : '#fff',
              border: `1px solid ${isDark ? '#555' : '#ddd'}`,
              borderRadius: 8
            }}
            formatter={(value) => [Number(value) || 0, 'Employees']}
          />
          <Bar
            dataKey="count"
            radius={[0, 4, 4, 0]}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ============================================
// Gender Chart Component (Donut)
// ============================================

interface GenderChartProps {
  data: ChartDataItem[];
  loading?: boolean;
  height?: number;
}

export const GenderChart: React.FC<GenderChartProps> = ({
  data,
  loading = false,
  height = 300
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const chartData = data.map(item => ({
    name: formatGenderLabel(item.gender || item.name || 'Unknown'),
    value: item.count,
    fill: GENDER_COLORS[item.gender as keyof typeof GENDER_COLORS] || '#9E9E9E'
  }));

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  if (loading) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Skeleton variant="circular" width={height - 60} height={height - 60} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            paddingAngle={2}
            dataKey="value"
            label={(props: any) => `${props.name} (${(props.percent * 100).toFixed(0)}%)`}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#333' : '#fff',
              border: `1px solid ${isDark ? '#555' : '#ddd'}`,
              borderRadius: 8
            }}
            formatter={(value) => [Number(value) || 0, 'Employees']}
          />
          <Legend />
          {/* Center text */}
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={theme.palette.text.primary}
            fontSize={24}
            fontWeight="bold"
          >
            {total}
          </text>
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ============================================
// Experience Chart Component
// ============================================

interface ExperienceChartProps {
  data: ChartDataItem[];
  loading?: boolean;
  height?: number;
}

export const ExperienceChart: React.FC<ExperienceChartProps> = ({
  data,
  loading = false,
  height = 300
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const chartData = data.map((item, index) => ({
    name: item.experience_band || item.name || 'Unknown',
    count: item.count,
    fill: EXPERIENCE_COLORS[index % EXPERIENCE_COLORS.length]
  }));

  if (loading) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Skeleton variant="rectangular" width="100%" height={height - 40} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
        >
          <defs>
            <linearGradient id="experienceGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2196F3" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#2196F3" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#444' : '#eee'} />
          <XAxis
            dataKey="name"
            stroke={theme.palette.text.secondary}
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis stroke={theme.palette.text.secondary} />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#333' : '#fff',
              border: `1px solid ${isDark ? '#555' : '#ddd'}`,
              borderRadius: 8
            }}
            formatter={(value) => [Number(value) || 0, 'Employees']}
          />
          <Area
            type="monotone"
            dataKey="count"
            stroke="#2196F3"
            fill="url(#experienceGradient)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ============================================
// Age Distribution Chart Component
// ============================================

interface AgeChartProps {
  data: ChartDataItem[];
  loading?: boolean;
  height?: number;
}

export const AgeChart: React.FC<AgeChartProps> = ({
  data,
  loading = false,
  height = 300
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const chartData = data.map((item, index) => ({
    name: item.age_band || item.name || 'Unknown',
    count: item.count,
    fill: AGE_COLORS[index % AGE_COLORS.length]
  }));

  if (loading) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Skeleton variant="rectangular" width="100%" height={height - 40} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#444' : '#eee'} />
          <XAxis
            dataKey="name"
            stroke={theme.palette.text.secondary}
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis stroke={theme.palette.text.secondary} />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#333' : '#fff',
              border: `1px solid ${isDark ? '#555' : '#ddd'}`,
              borderRadius: 8
            }}
            formatter={(value) => [Number(value) || 0, 'Employees']}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ============================================
// Combined Workforce Analytics Widget
// ============================================

export const WorkforceAnalyticsWidget: React.FC<WorkforceChartsProps> = ({
  departmentData = [],
  genderData = [],
  experienceData = [],
  ageData = [],
  loading = false
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Card
      sx={{
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header with Tabs */}
        <Box sx={{ borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}` }}>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Workforce Analytics
            </Typography>
          </Box>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1 }}
          >
            <Tab label="Department" sx={{ textTransform: 'none', minWidth: 100 }} />
            <Tab label="Gender" sx={{ textTransform: 'none', minWidth: 100 }} />
            <Tab label="Experience" sx={{ textTransform: 'none', minWidth: 100 }} />
            <Tab label="Age" sx={{ textTransform: 'none', minWidth: 100 }} />
          </Tabs>
        </Box>

        {/* Chart Content */}
        <Box sx={{ p: 2 }}>
          {activeTab === 0 && (
            <DepartmentChart data={departmentData} loading={loading} height={280} />
          )}
          {activeTab === 1 && (
            <GenderChart data={genderData} loading={loading} height={280} />
          )}
          {activeTab === 2 && (
            <ExperienceChart data={experienceData} loading={loading} height={280} />
          )}
          {activeTab === 3 && (
            <AgeChart data={ageData} loading={loading} height={280} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================
// Helper Functions
// ============================================

function formatGenderLabel(gender: string): string {
  const labels: { [key: string]: string } = {
    male: 'Male',
    female: 'Female',
    other: 'Other',
    not_specified: 'Not Specified',
    prefer_not_to_say: 'Prefer Not to Say'
  };
  return labels[gender] || gender;
}

export default WorkforceAnalyticsWidget;
